import { useState } from 'react';
import api from 'api';
import { actions } from 'store';
import { useDispatch, useReduxState } from 'hooks';
import { getCustomAttributesTypes } from 'lib/customAttributes';
import { ModalButtonWithForm } from 'components/common';
import { Code, FormGroup, Switch } from 'components/common/form';

import type { UserCustomAttribute } from 'shared-types';

interface Props {
  existingAttributes: UserCustomAttribute[];
  updateData: () => Promise<void>;
}

const UserAttributesImport = ({ existingAttributes, updateData }: Props) => {
  const { dispatch } = useDispatch();
  const { messages, theme } = useReduxState();

  const [attributesFromData, setAttributesFromData] = useState<
    UserCustomAttribute[]
  >([]);

  const handleChange = (value) => {
    try {
      const parsedAttributes = JSON.parse(value);

      if (parsedAttributes?.length) {
        setAttributesFromData(parsedAttributes);
      }
    } catch {}
  };

  const handleHide = () => {
    setAttributesFromData([]);
  };

  const handleSubmit = async ({ attributes, data }) => {
    const parsedAttributes = JSON.parse(data);

    const includedAttributes = parsedAttributes.filter(
      (attribute) => attributes?.[attribute.id]?.includeInImport
    );

    for (const values of includedAttributes) {
      const data = {
        appliesTo: values.appliesTo,
        conditions: values.conditions,
        description: values.description,
        descriptionTranslations: values.descriptionTranslations,
        excludeFromExcelExport: values.excludeFromExcelExport,
        isEnabled: values.isEnabled,
        isRequired: values.isRequired,
        key: values.key,
        max: values.max,
        min: values.min,
        optionGroups: values.optionGroups?.map((optionGroup) => ({
          ...optionGroup,
          options: optionGroup.options?.map((option) => ({
            key: option.key,
            label: option.label,
            labelTranslations: option.labelTranslations
          }))
        })),
        permissions: values.permissions,
        showBeforeRegistration: values.showBeforeRegistration,
        title: values.title,
        titleTranslations: values.titleTranslations,
        type: values.type,
        validation: values.validation
      };

      await api.post('/admin/user-custom-attributes', data);
    }

    await updateData();

    dispatch(
      actions.addNotice({
        text: 'Attributes imported successfully',
        type: 'success'
      })
    );
  };

  const validateData = (value) => {
    try {
      JSON.parse(value);
      return undefined;
    } catch (error) {
      return 'Invalid JSON';
    }
  };

  return (
    <ModalButtonWithForm
      buttonCategory="default"
      buttonIcon="file-import"
      buttonIsDropdown={true}
      buttonSize="header"
      buttonText={'Import attributes'}
      modalActionText={messages.commonImport}
      modalTitle={'Import attributes'}
      modalWidth="medium"
      onHide={handleHide}
      onSubmit={handleSubmit}
      pristineIsValid={false}
    >
      {(formState) => (
        <div>
          <FormGroup label={messages.commonData} maxWidth="100%">
            <Code
              autoGrow={false}
              field="data"
              id="user-attributes-import-data"
              onValueChange={handleChange}
              placeholder={messages.commonPasteData}
              rows={7}
              validate={validateData}
            />
          </FormGroup>

          {attributesFromData?.length ? (
            <FormGroup label={messages.commonAttributes} maxWidth="100%">
              {attributesFromData.map((attribute) => (
                <div
                  className={`attribute ${
                    existingAttributes.some(
                      (existingAttribute) =>
                        existingAttribute.key === attribute.key
                    )
                      ? 'attribute-disabled'
                      : ''
                  }`}
                  key={attribute.id}
                >
                  <Switch
                    disabled={existingAttributes.some(
                      (existingAttribute) =>
                        existingAttribute.key === attribute.key
                    )}
                    field={`attributes.${attribute.id}.includeInImport`}
                    id={`user-attributes-import-${attribute.id}-include`}
                  />

                  <div className="attribute-title">
                    {attribute.title}

                    <div className="attribute-key">{attribute.key}</div>
                  </div>

                  <div className="attribute-type">
                    {getCustomAttributesTypes({ messages })[attribute.type]}
                  </div>
                </div>
              ))}
            </FormGroup>
          ) : null}

          <style jsx>{`
            .attribute {
              align-items: center;
              border-top: 1px solid ${theme.lightGrey};
              display: flex;
              flex-wrap: wrap;
              gap: 10px;
              padding: 10px 0;
            }
            .attribute-disabled {
              opacity: 0.5;
            }
            .attribute-title {
              flex-grow: 1;
              font-weight: 600;
            }
            .attribute-key {
              color: ${theme.textColorLighter};
              font-size: 1.2rem;
              font-weight: 400;
              margin-top: 2px;
              white-space: nowrap;
            }
            .attribute-type {
              min-width: 200px;
            }
          `}</style>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default UserAttributesImport;
